import { useEffect, useMemo, useState } from "react";
import { MousePointerClick } from "lucide-react";

/**
 * DocumentStatsToast — small horizontal pill INSIDE the document
 * environment showing letter / word / page / file-size counts. Iter39
 * user clarification: "the count should be a very small toast INSIDE
 * the document environment stacked horizontally."
 *
 * Renders absolutely positioned in the lower-left of the parent doc
 * area (the parent must be `position: relative`). Switches to selection
 * counts when text is highlighted in the editor.
 */
const WORDS_PER_PAGE = 250;

export default function DocumentStatsToast({ editorRef, body, name }) {
  const [selectionText, setSelectionText] = useState("");
  const [tick, setTick] = useState(0);
  const [visible, setVisible] = useState(() => localStorage.getItem("cresc-stats-visible") !== "false");

  const toggle = () => setVisible(v => {
    const next = !v;
    localStorage.setItem("cresc-stats-visible", String(next));
    return next;
  });

  useEffect(() => {
    const onSelChange = () => {
      try {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
          setSelectionText(""); return;
        }
        const ed = editorRef?.current;
        if (!ed) { setSelectionText(""); return; }
        if (!ed.contains(sel.anchorNode) || !ed.contains(sel.focusNode)) {
          setSelectionText(""); return;
        }
        setSelectionText((sel.toString() || "").trim());
      } catch { setSelectionText(""); }
    };
    document.addEventListener("selectionchange", onSelChange);
    return () => document.removeEventListener("selectionchange", onSelChange);
  }, [editorRef]);

  useEffect(() => {
    const t = setTimeout(() => setTick((x) => x + 1), 100);
    return () => clearTimeout(t);
  }, [body]);

  const stats = useMemo(() => {
    const ed = editorRef?.current;
    const fullText = ed ? (ed.innerText || ed.textContent || "") : "";
    const text = selectionText || fullText;
    const isSelection = !!selectionText;
    const letters = (text.match(/\S/g) || []).length;
    const words = text.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
    const pages = Math.max(1, Math.ceil(words / WORDS_PER_PAGE));
    const bytes = (text || "").length;
    return { letters, words, pages, bytes, isSelection };
  }, [tick, selectionText, editorRef, body]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="absolute bottom-3 left-3 z-[40] bg-[#141414]/90 backdrop-blur border border-[#2A2A2A] rounded-full shadow-md px-2.5 py-1 flex items-center gap-2 text-[10px] text-[#A1A1AA] font-mono cursor-pointer hover:border-[#5A90F5]/40 select-none"
      data-testid="doc-stats-toast"
      title={visible ? (stats.isSelection ? "Selection stats · click to hide" : `${name || "Document"} stats · click to hide`) : "Click to show stats"}
      onClick={toggle}
    >
      {!visible ? (
        <span className="text-[#52525B] text-[9px]">stats</span>
      ) : (
        <>
          {stats.isSelection && <MousePointerClick className="w-2.5 h-2.5 text-[#5A90F5]" />}
          <span><span className="text-[#EDEDED]">{stats.words.toLocaleString()}</span>w</span>
          <span className="text-[#2A2A2A]">·</span>
          <span><span className="text-[#EDEDED]">{stats.letters.toLocaleString()}</span>c</span>
          <span className="text-[#2A2A2A]">·</span>
          <span><span className="text-[#EDEDED]">{stats.pages}</span>pg</span>
          <span className="text-[#2A2A2A]">·</span>
          <span className="text-[#EDEDED]">{formatBytes(stats.bytes)}</span>
        </>
      )}
    </div>
  );
}

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
